'use client'

import { motion } from 'framer-motion'
import 'keen-slider/keen-slider.min.css'
import { type KeenSliderInstance, useKeenSlider } from 'keen-slider/react'
import Image from 'next/image'
import { useEffect, useState } from 'react'

import { cn } from '@src/utils/tailwind'

export interface QuoteReviewsProps {
  className?: string
  backgroundColor?: string
  mainQuoteColor?: string
  secondaryQuoteColor?: string
  alignment?: 'center' | 'auto'
  reviews: Array<{
    mainQuote: string
    secondaryQuote?: string
    imageQuoteUrl?: string
    imageQuoteAlt?: string
    thumbnailUrl?: string
    thumbnailAlt?: string
  }>
}

export function QuoteReviews(props: QuoteReviewsProps) {
  const [slider, setSlider] = useState<KeenSliderInstance | null>(null)
  const [ref] = useKeenSlider<HTMLDivElement>(
    {
      loop: true,
      drag: true,
      mode: 'free',
      renderMode: 'performance',
      slides: {
        perView: 'auto',
        origin: props.alignment ?? 'center',
        spacing: 40,
      },
    },
    [
      function autoplayController(slider) {
        const animation = { duration: props.reviews.length * 5000, easing: (t: number) => t }
        let overflow = false

        setSlider(slider)

        function setOverflow() {
          overflow = slider.track.details.slidesLength > 1
          slider.update({
            ...slider.options,
            loop: overflow,
            drag: overflow,
            slides: {
              ...(slider.options.slides as any),
              origin: overflow ? (props.alignment ?? 'center') : 'auto',
            },
          })
          if (!overflow) {
            const center = (1 - slider.track.details.slidesLength) / -2
            slider.track.to(center)
          }
        }

        slider.on('created', () => {
          setOverflow()
          if (!overflow) return
          slider.moveToIdx(props.reviews.length - 1, true, animation)
        })
        slider.on('updated', () => {
          setOverflow()
          if (!overflow) return
          slider.moveToIdx(slider.track.details.abs + props.reviews.length - 1, true, animation)
        })
        slider.on('animationEnded', () => {
          if (!overflow) return
          slider.moveToIdx(slider.track.details.abs + props.reviews.length - 1, true, animation)
        })
      },
    ]
  )

  useEffect(() => {
    if (slider) {
      slider.update(slider.options)
      slider.emit('updated')
    }
  }, [slider, props.reviews.length])

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: slider ? 1 : 0 }}
      className={cn('keen-slider flex items-start', props.className, {
        'gap-10': !slider,
      })}
      data-keen-slider-disabled={true}
    >
      {props.reviews.map((review, index) => (
        <QuoteReview
          key={index}
          className="keen-slider__slide"
          backgroundColor={props.backgroundColor}
          mainQuote={review.mainQuote}
          mainQuoteColor={props.mainQuoteColor}
          secondaryQuote={review.secondaryQuote}
          secondaryQuoteColor={props.secondaryQuoteColor}
          imageQuoteUrl={review.imageQuoteUrl}
          imageQuoteAlt={review.imageQuoteAlt}
          thumbnailUrl={review.thumbnailUrl}
          thumbnailAlt={review.thumbnailAlt}
        />
      ))}
    </motion.div>
  )
}

interface QuoteReviewProps {
  className?: string
  mainQuote: string
  mainQuoteColor?: string
  secondaryQuote?: string
  secondaryQuoteColor?: string
  imageQuoteUrl?: string
  imageQuoteAlt?: string
  thumbnailUrl?: string
  thumbnailAlt?: string
  backgroundColor?: string
}

function QuoteReview(props: QuoteReviewProps) {
  return (
    <div
      className={cn(
        props.className,
        'flex !min-h-[152px] min-w-[calc(100%-2rem)] gap-4 rounded-xl p-4 lg:min-w-[443px] lg:max-w-[443px]'
      )}
      style={{ backgroundColor: props.backgroundColor }}
    >
      {props.thumbnailUrl && (
        <Image
          className="size-[120px] rounded-xl"
          width={120}
          height={120}
          src={props.thumbnailUrl}
          alt={props.thumbnailAlt ?? ''}
        />
      )}
      <div className="flex flex-1 flex-col justify-center gap-1">
        {props.imageQuoteUrl && (
          <Image
            className="w-[80px] object-contain object-left"
            width={80}
            height={80}
            src={props.imageQuoteUrl}
            alt={props.imageQuoteAlt ?? ''}
          />
        )}
        <div className="text-heading-4" style={{ color: props.mainQuoteColor }}>
          {props.mainQuote}
        </div>
        {props.secondaryQuote && (
          <div className="text-body-md" style={{ color: props.secondaryQuoteColor }}>
            {props.secondaryQuote}
          </div>
        )}
      </div>
    </div>
  )
}
